import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';

import { FieldBlock } from '@/components/services/wizard/field';
import { MemberAvatar, joinFirstNames } from '@/components/team/member-avatar';
import { SheetStepFooter, TeamSheet } from '@/components/team/team-sheet';
import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useMembership } from '@/hooks/use-membership';
import {
  useOfferCatalogItem,
  useUnofferCatalogItem,
  useUpdateCatalogItem,
} from '@/hooks/use-service-catalog';
import { useTeamMembers } from '@/hooks/use-team';
import { useActingProviderStore } from '@/store/actingProviderStore';
import { useToastStore } from '@/store/toastStore';
import { useAuthStore } from '@/store/authStore';
import { CatalogItem } from '@/types/service-catalog';
import { getApiErrorMessage } from '@/utils/api-error';
import { canEditCatalogIdentity } from '@/utils/catalog-permissions';
import { formatCurrency } from '@/utils/format-currency';

/**
 * O serviço do negócio, sem tela própria: o dono mexe na identidade e em quem
 * faz; o profissional só diz se também faz — uma pergunta só não merece uma
 * página. O preço, a duração e a agenda continuam na cópia de cada um.
 *
 * Com o foco em outro profissional (o dono "vendo como" alguém), a folha é a
 * dele: quem está em foco só liga e desliga a si mesmo.
 */
export function CatalogItemSheet({
  item,
  open,
  onClose,
}: {
  item: CatalogItem;
  open: boolean;
  onClose: () => void;
}) {
  const { isOwner } = useMembership();
  const acting = useActingProviderStore((s) => s.acting);
  const selfProviderId = useAuthStore((s) => s.user?.providerProfile?.id ?? null);
  /** O profissional em foco: é dele o "eu faço" que a API grava. */
  const focusedProviderId = acting?.providerId ?? selfProviderId;
  // Vendo como outro profissional, o dono não manda: a folha é a de quem está
  // em foco, senão ele tiraria o serviço de alguém sem perceber.
  const asOwner = isOwner && !acting;

  const { data: members } = useTeamMembers({ enabled: asOwner });
  const { addToast } = useToastStore();

  const updateItem = useUpdateCatalogItem();
  const offer = useOfferCatalogItem();
  const unoffer = useUnofferCatalogItem();

  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description);
  /** O switch responde ao toque, não à volta da API; `null` = o que o servidor diz. */
  const [mineDraft, setMineDraft] = useState<boolean | null>(null);
  const mine = mineDraft ?? !!item.mine;

  useEffect(() => {
    if (!open) return;
    setName(item.name);
    setDescription(item.description);
  }, [open, item.name, item.description]);

  // Solta o palpite assim que o servidor concorda com ele.
  useEffect(() => {
    setMineDraft((draft) => (draft === null || draft === !!item.mine ? null : draft));
  }, [item.mine]);

  // Só o salvar trava a folha: marcar "eu faço" responde na hora e corre sozinho.
  const busy = updateItem.isPending;

  const handleSave = async () => {
    try {
      await updateItem.mutateAsync({ id: item.id, name: name.trim(), description });
      addToast('Serviço atualizado.', 'success');
      onClose();
    } catch (e) {
      addToast(getApiErrorMessage(e, 'Erro ao salvar o serviço.'), 'error');
    }
  };

  /** Sem `providerIds` é "eu faço": vale para quem está em foco. */
  const handleToggleMine = async (willOffer: boolean) => {
    setMineDraft(willOffer);
    try {
      if (willOffer) await offer.mutateAsync({ id: item.id });
      else if (focusedProviderId)
        await unoffer.mutateAsync({ id: item.id, providerId: focusedProviderId });
    } catch (e) {
      setMineDraft(null);
      addToast(getApiErrorMessage(e, 'Não foi possível mudar isso agora.'), 'error');
    }
  };

  const handleToggleMember = async (providerId: string, willOffer: boolean) => {
    try {
      if (willOffer) await offer.mutateAsync({ id: item.id, providerIds: [providerId] });
      else await unoffer.mutateAsync({ id: item.id, providerId });
    } catch (e) {
      addToast(getApiErrorMessage(e, 'Não foi possível mudar quem faz.'), 'error');
    }
  };

  const whoLabel = item.professionals.length
    ? joinFirstNames(item.professionals.map((p) => p.name))
    : 'Ninguém faz ainda';

  if (!asOwner) {
    // Quem criou o item e ainda é o único a fazer mexe no texto dele; o que já
    // virou serviço da casa (outro profissional faz, ou nasceu com o dono) só
    // se lê — é a mesma regra que a API aplica.
    const canEditIdentity = canEditCatalogIdentity(item, {
      isOwner: false,
      providerId: focusedProviderId,
    });

    return (
      <TeamSheet
        open={open}
        onClose={onClose}
        title={item.name}
        subtitle="Serviço da equipe"
        busy={canEditIdentity ? busy : undefined}
        footer={
          canEditIdentity ? (
            <SheetStepFooter
              onNext={handleSave}
              nextLabel="Salvar"
              busy={updateItem.isPending}
              onBack={onClose}
              backLabel="Fechar"
            />
          ) : undefined
        }
      >
        <View className="gap-4 pb-2">
          {canEditIdentity ? (
            <>
              <FieldBlock title="Nome">
                <Input value={name} onChangeText={setName} placeholder="Corte simples" />
              </FieldBlock>
              <FieldBlock title="Descrição">
                <Textarea
                  value={description}
                  onChangeText={setDescription}
                  placeholder="O que está incluído"
                />
              </FieldBlock>
            </>
          ) : item.description ? (
            <ThemedText className="text-sm text-neutral-600">{item.description}</ThemedText>
          ) : null}

          <View className="flex-row items-center gap-3 rounded-2xl bg-neutral-50 px-4 py-3">
            <Ionicons className="text-neutral-500" name="people-outline" size={16} />
            <ThemedText className="flex-1 text-sm text-neutral-600">{whoLabel}</ThemedText>
          </View>

          <View className="flex-row items-center gap-3 rounded-2xl border border-neutral-200 px-4 py-3">
            <View className="flex-1 gap-0.5">
              <ThemedText className="font-semibold text-foreground">Eu também faço</ThemedText>
              <ThemedText className="text-xs text-neutral-500">
                Entra na sua lista de serviços, com o seu preço e a sua agenda.
              </ThemedText>
            </View>
            <Switch checked={mine} onCheckedChange={handleToggleMine} />
          </View>

          {canEditIdentity ? (
            <ThemedText className="text-xs text-neutral-500">
              Você criou este serviço. Quando outro profissional passar a fazê-lo, o texto vira da
              equipe e quem edita é o dono.
            </ThemedText>
          ) : (
            <ThemedText className="text-xs text-neutral-500">
              O nome e a descrição são da equipe: quem edita é o dono.
            </ThemedText>
          )}
        </View>
      </TeamSheet>
    );
  }

  const activeMembers = (members ?? []).filter((m) => m.status === 'ACTIVE' && m.attends);

  return (
    <TeamSheet
      open={open}
      onClose={onClose}
      title={item.name}
      subtitle="Como este serviço aparece para o cliente"
      busy={busy}
      footer={
        <SheetStepFooter
          onNext={handleSave}
          nextLabel="Salvar"
          busy={updateItem.isPending}
          onBack={onClose}
          backLabel="Fechar"
        />
      }
    >
      <View className="gap-4 pb-2">
        <FieldBlock title="Nome">
          <Input value={name} onChangeText={setName} placeholder="Corte simples" />
        </FieldBlock>
        <FieldBlock title="Descrição">
          <Textarea
            value={description}
            onChangeText={setDescription}
            placeholder="O que está incluído"
          />
        </FieldBlock>

        <View className="gap-3">
          <ThemedText className="font-semibold text-sm text-foreground">Quem faz</ThemedText>
          {activeMembers.length === 0 ? (
            <ThemedText className="text-sm text-neutral-500">
              Cadastre profissionais para dizer quem faz este serviço.
            </ThemedText>
          ) : (
            activeMembers.map((member) => {
              const doing = item.professionals.find((p) => p.providerId === member.providerId);
              return (
                <View key={member.id} className="flex-row items-center gap-3">
                  <MemberAvatar name={member.name} image={member.image} color={member.color} />
                  <View className="flex-1">
                    <ThemedText className="text-foreground">{member.name}</ThemedText>
                    {doing?.price != null && (
                      <ThemedText className="text-xs text-neutral-500">
                        {formatCurrency(doing.price)}
                      </ThemedText>
                    )}
                  </View>
                  <Switch
                    checked={!!doing}
                    disabled={offer.isPending || unoffer.isPending}
                    onCheckedChange={(next) => handleToggleMember(member.providerId, next)}
                  />
                </View>
              );
            })
          )}
        </View>
      </View>
    </TeamSheet>
  );
}
